import React, { useEffect, useState } from "react";

import { Card, Input, PageHeader, Button } from "@cofano/cck";
import FormField from "components/cck/FormField";
import { Controller, useForm } from "react-hook-form";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";

import { Language, usePermission } from "imports/Utils";
import { gql } from "__generated__/gql";
import { useQuery, useMutation } from "@apollo/client";

import "./mappings.scss";
import { intl } from "LocaleWrapper";
import Helmet from "components/helmet/Helmet";

// When adding new mapping type, just add it to this list
const types = [
	"REEFER",
	"UNCATEGORIZED",
];

const Mappings = () => {

	const { isAdmin, isMasterdata } = usePermission();

	const [ editingRow, setEditingRow ] = useState();
	const [ newRow, setNewRow ] = useState();

	useEffect(() => {
		if (editingRow == undefined && newRow) {
			setNewRow(undefined);
		}
	}, [ editingRow ]);

	const { refetch, data } = useQuery(MAPPINGS_QUERY);

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("MAPPINGS")}
					<Helmet title={intl.formatMessage({ id: "MAPPINGS" })} />
				</PageHeader.Title>
			</PageHeader>

			<Card disablePadding>
				{((isAdmin || isMasterdata) && data?.mappings) && types.map(type => {
					const filteredMappings = data.mappings.filter(mapping => mapping.type == type);
					return <Accordion key={type} defaultExpanded>
						<AccordionSummary
							expandedDefault
							expandIcon={<ExpandMoreIcon />}>
							<div className="mapping-header">
								<div className="mapping-title">{Language(`MAPPING_${type}`)}</div>
								<div className="mapping-description">{Language(`MAPPING_${type}_DESCRIPTION`)}</div>
							</div>
						</AccordionSummary>
						<AccordionDetails style={{ padding: "0px" }}>
							<div className="mapping-category">
								{filteredMappings.map(mapping =>
									<MappingRow
										key={mapping.id}
										disableEdit={editingRow != undefined && mapping.id != editingRow}
										refetch={refetch}
										setEditingRow={setEditingRow}
										mapping={mapping} />,
								)}

								{(newRow && editingRow == 0) ?
									newRow.type == type && <MappingRow
										mapping={newRow}
										disableEdit={false}
										refetch={refetch}
										setEditingRow={setEditingRow} />
									:
									<div
										className="mapping-row create"
										onClick={() => {
											setNewRow({ id: 0, key: undefined, value: undefined, type: type });
											setEditingRow(0);
										}}>
										<div>{Language("ADD_MAPPING")}</div>
										<AddIcon />
									</div>}
							</div>
						</AccordionDetails>
					</Accordion>;
				})}
			</Card>
		</div>
	);
};

const MappingRow = ({ disableEdit, mapping: input, refetch, setEditingRow }) => {

	const { control, handleSubmit, formState, watch } = useForm({
		defaultValues: input,
	});

	const [ editMode, setEditMode ] = useState(input.id == 0);

	const [ deleteMapping ] = useMutation(DELETE_MAPPING_MUTATION, {
		onCompleted: () => {
			refetch();
		},
	});

	const [ saveMapping ] = useMutation(INSERT_OR_UPDATE_MAPPING_MUTATION, {
		onCompleted: () => {
			refetch();
			setEditMode(false);
		},
	});

	const save = (data) => {
		saveMapping({
			variables: { mapping: data },
		});
	};

	useEffect(() => setEditingRow(editMode ? values.id : undefined), [ editMode ]);

	const values = watch();

	return <form onSubmit={handleSubmit(save)}>
		<div className="mapping-row existing" key={1}>
			<div className="mapping-content">
				<FormField errors={formState.errors} name={"key"}>
					<Controller
						name="key"
						control={control}
						rules={{ required: true }}
						render={({ field }) => editMode ? <Input {...field} placeholder={intl.formatMessage({ id: "KEY" })} /> : <div>{values.key}</div>} />
				</FormField>
				<FormField errors={formState.errors} name={"value"}>
					<Controller
						name="value"
						control={control}
						rules={{ required: true }}
						render={({ field }) => editMode ? <Input {...field} placeholder={intl.formatMessage({ id: "VALUE" })} /> : <div>{values.value}</div>} />
				</FormField>
			</div>
			<div style={{ display: "flex" }}>
				{editMode ? (
					<React.Fragment>
						<Button type="submit" style={{ marginRight: 4 }} size="small">
							{Language("SAVE")}
						</Button>

						<Button
							onClick={() => setEditMode(false)}
							style={{ marginRight: 4 }}
							size="small"
							variant="outlined">
							{Language("CANCEL")}
						</Button>
					</React.Fragment>
				) : (
					<Button
						disabled={disableEdit}
						onClick={() => setEditMode(true)}
						style={{ marginRight: 4 }}
						size="small"
						variant="outlined">
						{Language("EDIT")}
					</Button>
				)}

				{!!values.id && (
					<Button
						disabled={disableEdit || editMode}
						onClick={() => deleteMapping({ variables: { id: values.id } })}
						size="small"
						variant="outlined">
						{Language("DELETE")}
					</Button>
				)}
			</div>
		</div>
	</form>;
};

const MAPPINGS_QUERY = gql(`#graphql
	query mappings {
		mappings {
			id
            key
            value
            type
		}
	}
`);

const INSERT_OR_UPDATE_MAPPING_MUTATION = gql(`#graphql
	mutation mapping($mapping: MappingInput!) {
		mapping(mapping: $mapping) {
            id
        }
	}
`);

const DELETE_MAPPING_MUTATION = gql(`#graphql
	mutation deleteMapping($id: Long!) {
		deleteMapping(id: $id)
	}
`);

export default Mappings;
